import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLandingData } from "../../../hooks/useLandingData";

export default function WallOfLove() {
  const { t } = useTranslation();
  const { testimonials, liveMetrics } = useLandingData();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="py-28"
      style={{ background: "linear-gradient(180deg, #F5F0EA 0%, #FAFAF8 100%)" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">
            {t("wol_label")}
          </span>
          <h2
            className="text-4xl lg:text-5xl font-bold text-stone-900 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("wol_title_1")}
            <br />
            <span className="text-amber-500">{t("wol_title_2")}</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-4 gap-5 mb-10">
          {liveMetrics.map((m, i) => (
            <div
              key={m.value}
              className={`bg-stone-900 rounded-2xl p-6 text-center transition-all duration-500 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <p className="text-3xl font-black text-amber-400 mb-2">{m.value}</p>
              <p className="text-stone-400 text-xs leading-snug">{m.label}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          {testimonials.map((testimonial, i) => (
            <div
              key={testimonial.id}
              className={`bg-white border border-stone-100 rounded-3xl p-8 hover:-translate-y-1 transition-all duration-500 cursor-default ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${200 + i * 100}ms` }}
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex gap-1">
                  {Array.from({ length: testimonial.rating }).map((_, j) => (
                    <i key={j} className="ri-star-fill text-amber-400 text-sm" />
                  ))}
                </div>
                <span className="text-xs font-semibold text-stone-400 border border-stone-200 px-3 py-1 rounded-full">
                  {testimonial.platform}
                </span>
              </div>

              <blockquote className="text-stone-700 text-base leading-relaxed mb-6 italic">
                &ldquo;{testimonial.quote}&rdquo;
              </blockquote>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-10 h-10 rounded-full object-cover object-top"
                  />
                  <div>
                    <p className="font-semibold text-stone-900 text-sm">{testimonial.name}</p>
                    <p className="text-stone-400 text-xs">{testimonial.role} · {testimonial.company}</p>
                  </div>
                </div>
                <div className="px-3 py-1 bg-emerald-50 border border-emerald-100 rounded-full">
                  <span className="text-emerald-600 text-xs font-semibold">{testimonial.metrics}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-amber-500 rounded-3xl p-8 lg:p-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">
                {t("wol_cta_title")}
              </h3>
              <p className="text-amber-100">{t("wol_cta_desc")}</p>
            </div>
            <button
              onClick={() => {
                const el = document.querySelector("#precios");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="flex-shrink-0 px-8 py-3.5 bg-white text-amber-600 font-bold rounded-xl hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("wol_cta_btn")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
